import { useEffect, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Icon } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { PickerModal, TextEditor } from './editors';
import { SettingsUnreadable } from './Unreadable';
import { usePatientIdFormat, useSavePatientIdFormat } from './useSettings';

/**
 * Clinic manager · Settings ▸ Patient IDs — `g_set_ids.dc.html`.
 *
 * "NEXT PATIENT" IS A PREVIEW, NOT A RESERVATION. It is the number the
 * auto-MRN would hand out right now — the same high-water-mark query
 * `PatientsService` runs when it actually mints one, counting soft-deleted
 * rows because the unique index still counts them. It takes nothing, so two
 * managers on this screen see the same answer, which is the truth.
 *
 * It is also why the card is a GHOST: the other two are settings, this one is
 * a consequence of them, and it re-reads when either changes.
 *
 * The random strategy HAS no next number — that is what non-sequential means —
 * so the card says so rather than printing an invented code (§4.9).
 */
export function ManagerPatientIdsPage() {
  const go = useNavigate();
  const { value, isLoading, error, refetch } = usePatientIdFormat();
  const save = useSavePatientIdFormat();
  const [prefix, setPrefix] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<'sequential' | 'random' | null>(null);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [bad, setBad] = useState<string | null>(null);

  usePageMeta({ title: 'Patient IDs · System Settings' });

  const shownPrefix = prefix ?? value?.prefix ?? '';
  const shownStrategy = strategy ?? value?.strategy ?? 'sequential';
  const dirty = !!value
    && ((prefix !== null && prefix !== value.prefix)
      || (strategy !== null && strategy !== value.strategy));

  /* The draft is dropped once the server confirms it, so "unsaved" is about
     the FORM and not about a value that happens to differ from the last read. */
  useEffect(() => {
    if (!value) return;
    if (prefix !== null && prefix === value.prefix) setPrefix(null);
    if (strategy !== null && strategy === value.strategy) setStrategy(null);
  }, [value, prefix, strategy]);

  const reading = isLoading && !value;
  const unreadable = !reading && !value;

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const anyShown = ['prefix', 'numbering', 'next patient'].some((t) => matches(t));

  const crumb = usePhoneCrumb('Settings', 'Patient IDs');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'Patient IDs',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Patient IDs
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="ids" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: dirty ? {
        label: save.isPending ? 'Saving…' : 'Save',
        onClick: () => save.mutate(
          { prefix: shownPrefix, strategy: shownStrategy },
          { onSuccess: () => { setPrefix(null); setStrategy(null); } },
        ),
      } : undefined,
    },
    [query, dirty, save.isPending, unreadable],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s MRN format…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="patient ID format" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {value ? (
          <>
            {anyShown ? (
              <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
                The MRN format the auto-MRN reads from
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              {matches('prefix') ? (
                <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing(true)}>
                  <p className="t-ovl mut">Prefix</p>
                  <p className="t-strong mt4">{shownPrefix} <Icon name="pencil" size="sm" /></p>
                </Card>
              ) : null}
              {matches('numbering') ? (
                <Card style={{ flex: 1.5, margin: 0, cursor: 'pointer' }} onClick={() => setPicking(true)}>
                  <p className="t-ovl mut">Numbering</p>
                  <p className="t-strong mt4">
                    {shownStrategy === 'random' ? 'Random' : 'Sequential'} ▾
                  </p>
                </Card>
              ) : null}
              {matches('next patient') ? (
                <Card kind="ghost" className="cm-pid-next" style={{ flex: 1.5, margin: 0 }}>
                  <p className="t-ovl mut">Next patient</p>
                  <p className="t-strong mt4">
                    {/* A DRAFT HAS NO NEXT NUMBER YET. The preview is the
                        server's, computed for the SAVED format; while the form
                        is dirty it would be answering about a format nobody has
                        stored, so it says which instead of implying the new
                        prefix is already issuing numbers. */}
                    {dirty
                      ? <span className="mut">after Save</span>
                      : value.nextMrn ?? <span className="mut">no next — random has no sequence</span>}
                  </p>
                </Card>
              ) : null}
            </div>

            {!anyShown ? (
              <p className="t-sec mut" style={{ marginTop: 18 }}>
                No setting on this screen matches that. Clear the search to see the format again.
              </p>
            ) : null}

            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Existing charts keep their number · printed on every artifact, searchable
            </p>

            {bad ? <p className="t-sec red" style={{ marginTop: 10 }}>{bad}</p> : null}
            {save.isError ? (
              <p className="t-sec red" style={{ marginTop: 10 }}>
                That did not save. Nothing was changed on the server — try again, or reload to see
                what is stored.
              </p>
            ) : null}
            {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
          </>
        ) : null}
      </div>

      <TextEditor
        open={editing}
        title="Prefix"
        sub="what leads every auto-generated patient number"
        value={shownPrefix}
        onClose={() => setEditing(false)}
        onDone={(next) => {
          /* The server's own charset. Typing `#` and having `P-` come back
             silently is worse than being told here. */
          if (!next || next.length > 16 || !/^[A-Za-z0-9\-_/.]+$/.test(next)) {
            setBad('A prefix is 1–16 characters of letters, digits, - _ / or .');
            return;
          }
          setBad(null);
          setPrefix(next);
        }}
      />

      <PickerModal
        open={picking}
        title="Numbering"
        sub="how the next patient number is chosen"
        value={shownStrategy}
        options={[
          { value: 'sequential', label: 'Sequential', note: 'a readable running number — P-000001, P-000002' },
          { value: 'random', label: 'Random', note: 'a six-character code with no order in it — nobody can infer how many patients you have' },
        ]}
        onClose={() => setPicking(false)}
        onPick={(next) => setStrategy(next as 'sequential' | 'random')}
      />
    </div>
  );
}
